// src/web/client/src/utils/planEntitlements.js
//
// The frontend does NOT decide what a plan is entitled to.
//
// GET /subscription/info returns `features` — the FEATURE_ACCESS projection of
// the backend's config/plans.js, which is the single source of truth for plan
// entitlements. This module is a thin, defensive reader of that payload.
//
// WHY IT EXISTS. Five frontend surfaces used to answer "is this merchant
// entitled?" by comparing the plan STRING to 'pro' or 'basic':
//   SubscriptionGuard.useSubscriptionStatus  hasFeature() → isPro ? true : ...
//   Layout                                   nav gating + the plan badge
//   ProTools                                 the whole page
//   Analytics                                whether to fetch at all
// When PR #198 added the `scale` tier ($99.99, above pro at $49.99), none of
// them recognised it: a Scale merchant saw an "Upgrade to Pro" wall on Pro
// Tools, an "Analytics requires Basic or Pro plan" wall, both nav items
// locked, and a badge reading "Trial". They were paying the most and being
// shown the least.
//
// Asking the server what the plan grants means a tier added to plans.js is
// inherited here with no frontend change at all — which is the property the
// string comparisons lacked.

// The Pro Tools page's tabs, each with the entitlement that unlocks it. The
// feature names are FEATURE_ACCESS keys, i.e. the backend's own vocabulary.
export const PRO_TOOL_FEATURES = {
  vip: 'vip_customers',
  sentiment: 'sentiment_analysis',
  timing: 'smart_timing',
  'ab-testing': 'ab_testing',
};

// True when the subscription payload says the plan grants `feature`.
//
// Defensive about the payload shape on purpose:
//   - `features` is an OBJECT map on the real endpoint
//     (middleware/subscriptionMiddleware getSubscriptionInfo), but the DEMO
//     middleware answers with a legacy ARRAY of strings including the sentinel
//     'all_features'. Both are handled rather than "fixed", because the demo
//     shop is a live surface and this reader must not be the thing that breaks
//     it. A demo shop keeps the full access it has today.
//   - a missing/failed payload grants NOTHING. Fail-safe: an entitlement
//     question that cannot be answered is answered "no", never "yes".
export function hasFeature(info, feature) {
  if (!info || !feature) return false;
  const features = info.features;
  if (!features) return false;
  if (Array.isArray(features)) {
    return features.includes('all_features') || features.includes(feature);
  }
  return features[feature] === true;
}

// True when the plan grants at least one of `features`. Used to decide whether
// a whole page has anything to show.
export function hasAnyFeature(info, features) {
  return (features || []).some((f) => hasFeature(info, f));
}

// The short plan label for the sidebar badge.
//
// Sourced from `plan_name` (PLAN_INFO[plan].name in config/plans.js) rather
// than from a plan-string ternary, which is what used to label a Scale
// merchant "Trial". The trailing " Plan" is trimmed and "Free Trial" shortened
// so every EXISTING plan renders exactly the string it rendered before:
//   pro → "Pro"   basic → "Basic"   trial → "Trial"   scale → "Scale"
// Anything unrecognised falls back to "Trial", the previous default.
export function planBadgeLabel(info) {
  const name = info?.plan_name;
  if (typeof name !== 'string' || !name.trim()) return 'Trial';
  const short = name.replace(/\s*Plan$/i, '').trim();
  if (/^free trial$/i.test(short) || !short) return 'Trial';
  return short;
}
